"use client"

import { useState, useRef, useEffect } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { loadStripe } from "@stripe/stripe-js"
import { Elements } from "@stripe/react-stripe-js"
import Payment from "../components/Checkout/Payment"

// Import images for Leafwell Nature Stay slider
import img3814 from "../images/IMG_3814.jpeg";
import img3855 from "../images/IMG_3855.jpeg";
import img3110 from "../images/IMG_3110.jpeg";
import img3080 from "../images/IMG_3080.jpeg";
import img3024 from "../images/IMG_3024.jpeg";
import img3753 from "../images/IMG_3753.jpeg";
import img3028 from "../images/IMG_3028.jpeg";
import img3094 from "../images/IMG_3094.jpeg";
import img2918 from "../images/IMG_2918.jpeg";
import img3005 from "../images/IMG_3005.jpeg";
import img3007 from "../images/IMG_3007.jpeg";
import img2969 from "../images/IMG_2969.jpeg";
import img2943 from "../images/IMG_2943.jpeg";

// Initialize Stripe with your publishable key from environment variable
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY 
)

// Enhanced event data with detailed information
const events = [
  {
    id: "leafwell-nature-stay",
    name: "Leafwell Nature Stay",
    price: 315,
    description: "Luxury Nature Suite in Upstate New York - Starts at $315/night (2+ nights) or $395/night (1 night) - Unplug. Settle in. Reconnect. Cold Plunge available now. Sauna available mid August.",
    duration: "1-5 Nights",
    maxGuests: 6,
    pricing: {
      oneNight: 395,
      twoNights: 315,
    },
    images: [
      "https://cdn.prod.website-files.com/65e0da8edae1c46e590f55e2/6621fb733cc945b04f7b482e_P1803285-p-2000.jpg",
      img3814,
      img3855,
      img3110,
      img3080,
      img3024,
      img3753,
      img3028,
      img3094,
      img2918,
      img3005,
      img3007,
      img2969,
      img2943,
    ],
    highlights: ["Private Lux Suite (Sleeps 2)", "PLUNGE brand Cold Plunge access (Sauna available mid August)", "Forest Trail + Stream", "Optional Dome (Sleeps 4 additional)"],
    availableDates: [
      "2025-07-01", "2025-07-02", "2025-07-03", "2025-07-04", "2025-07-05",
      "2025-07-08", "2025-07-09", "2025-07-10", "2025-07-11", "2025-07-12",
      "2025-07-15", "2025-07-16", "2025-07-17", "2025-07-18", "2025-07-19",
      "2025-07-22", "2025-07-23", "2025-07-24", "2025-07-25", "2025-07-26",
      "2025-07-29", "2025-07-30", "2025-07-31",
      "2025-08-01", "2025-08-02", "2025-08-05", "2025-08-06", "2025-08-07",
      "2025-08-08", "2025-08-09", "2025-08-12", "2025-08-13", "2025-08-14",
      "2025-08-15", "2025-08-16", "2025-08-19", "2025-08-20", "2025-08-21",
      "2025-08-22", "2025-08-23", "2025-08-26", "2025-08-27", "2025-08-28",
      "2025-08-29", "2025-08-30",
      "2025-09-02", "2025-09-03", "2025-09-04", "2025-09-05", "2025-09-06",
      "2025-09-09", "2025-09-10", "2025-09-11", "2025-09-12", "2025-09-13",
      "2025-09-16", "2025-09-17", "2025-09-18", "2025-09-19", "2025-09-20",
      "2025-09-23", "2025-09-24", "2025-09-25", "2025-09-26", "2025-09-27",
      "2025-09-30"
    ],
    upsells: [
      { id: "late-checkout", name: "Late Check Out (2:00 PM)", price: 100 },
      { id: "yoga-male", name: "Yoga Session (Male Instructor)", price: 200 },
      { id: "yoga-female", name: "Yoga Session (Female Instructor)", price: 200 },
      { id: "wellness-massage", name: "Wellness Massage", price: 300 },
      { id: "kosher-bison-dinner", name: "Kosher Meat Bison Bourbon Dinner Platter (includes 2 bottles prosecco) - Summer Only", price: 400 },
      { id: "therapist-session", name: "Therapist Session", price: 350 },
      { id: "medicine-ceremony-female", name: "Private Medicine Ceremony (Female Healer)", price: 1200 },
      { id: "dome-usage", name: "Dome Use", price: 180 },
      { id: "medicine-ceremony-male", name: "Private Medicine Ceremony (Male Healer)", price: 1200 },
    ],
    detailedInfo: {
      fullDescription: (
        <>
          <strong>An Overnight Forest Escape, Reimagined</strong>
          <br />
          <br />
          Nestled in a serene woodland just 90 minutes from NYC, Leafwell Farm offers a private hotel-style suite designed for couples or small groups seeking stillness, connection, and comfort in nature.
          <br />
          <br />
          The main suite features a luxurious full bathroom with a walk-in shower, a mini chef’s kitchen with refrigerator, a cozy sitting bar, and a queen bed for two. Large windows frame expansive views of open fields and forest, allowing the outdoors to flow in.
          <br />
          <br />
          Wellness experiences like massage, plant medicine ceremony, guided therapy, and meditation can be arranged to customize your stay.
          <br />
          <br />
          Step outside to enjoy your own cold plunge, fire pit, meditation area, picnic table, and kosher grill—all surrounded by 60 acres of trails and meadows.
          <br />
          <br />
          For larger groups, the adjacent geodome is available as an add-on. Fully heated and cooled, it comfortably sleeps 4–6 guests on plush Japanese-style floor mattresses—perfect for friends or family joining your retreat.
          <br />
          <br />
          One night here feels like a world away.
        </>
      ),
      schedule: [
        { time: "3:00 PM", activity: "Check-in to your Private Lux Suite" },
        { time: "4:00 PM", activity: "Forest trail exploration and stream meditation" },
        { time: "6:00 PM", activity: "Cold plunge and sauna session" },
        { time: "7:00 PM", activity: "Dinner at the fire pit (BBQ grill provided)" },
        { time: "Evening", activity: "Lock phone in provided timed phone safe, enjoy board games provided, bring a journal" },
        { time: "Morning", activity: "Wake up in nature, optional wellness activities" },
        { time: "12:30 PM", activity: "Check-out (select late checkout for 2:00 PM)" },
      ],
      included: [
        "Private Lux Suite (sleeps 2)",
        "Optional Dome usage (sleeps 4 additional)",
        "PLUNGE brand Cold Plunge access (Sauna available mid August)",
        "Forest trail and stream access",
        "Grill Fire Pit",
        "Kosher Grill (upon request)",
        "1 Bag of charcoal",
        "Firewood and starter",
        "Timed phone safe for digital detox",
        "Board games provided",
        "60 acres of serene nature",
        "Sacred silence and digital detox environment",
        "Complimentary parking",
      ],
      whatToBring: [
        "Comfortable walking shoes for forest trails",
        "Swimwear for cold plunge",
        "Warm clothes for evening",
        "Journal for reflection and evening activities",
        "Food for BBQ dinner at fire pit",
        "Pillows and blankets if staying in dome (Japanese floor mattresses provided)",
        "Open heart for reconnection",
      ],
      dietaryNotes:
        "This is an accommodation experience. Meals are not included but can be arranged separately. Kitchen facilities available in suite.",
      cancellationPolicy: "Full refund available up to 7 days before arrival. 50% refund within 3-7 days.",
    },
  },
]

// Event Details Modal Component
const EventDetailsModal = ({ event, isOpen, onClose, onSelect }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [isOpen])

  const [imageIndex, setImageIndex] = useState(0)

  // Track touch position for swipe gesture
  const touchStartXRef = useRef(0)
   
  const nextImage = () => {
    if (!event?.images) return
    setImageIndex((prev) => (prev + 1) % event.images.length)
  }

  const prevImage = () => {
    if (!event?.images) return
    setImageIndex((prev) => (prev - 1 + event.images.length) % event.images.length)
  }

  if (!isOpen || !event) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center rounded-t-2xl">
          <h2 className="text-xl font-bold text-gray-900">{event.name}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition duration-300">
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Hero Image and Basic Info */}
          <div className="mb-6">
            {event.images ? (
              <div className="relative h-96 rounded-xl overflow-hidden mb-4">
                {event.images.map((src, idx) => (
                  <div
                    key={idx}
                    className={`absolute inset-0 transition-opacity duration-700 ${idx === imageIndex ? 'opacity-100' : 'opacity-0'}`}
                  >
                    <img src={src} alt={`${event.name} ${idx + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}

                {/* Navigation Buttons */}
                <button
                  className="absolute left-2 md:left-6 top-1/2 transform -translate-y-1/2 block-circle z-10"
                  onClick={prevImage}
                >
                  <img src="/icons/prev.svg" alt="Previous" />
                </button>
                <button
                  className="absolute right-2 md:right-6 top-1/2 transform -translate-y-1/2 block-circle z-10"
                  onClick={nextImage}
                >
                  <img src="/icons/next.svg" alt="Next" />
                </button>

                {/* Swipe detection handlers for mobile */}
                <div
                  className="absolute inset-0"
                  onTouchStart={(e) => (touchStartXRef.current = e.touches[0].clientX)}
                  onTouchEnd={(e) => {
                    const touchEndX = e.changedTouches[0].clientX;
                    const diff = touchStartXRef.current - touchEndX;
                    if (Math.abs(diff) > 50) {
                      diff > 0 ? nextImage() : prevImage();
                    }
                  }}
                />

                <div className="absolute top-4 right-4 bg-white px-3 py-2 rounded-full shadow-lg">
                  <span className="text-lg font-bold text-green-600">${event.price}</span>
                </div>
              </div>
            ) : (
              <div className="relative h-96 rounded-xl overflow-hidden mb-4">
                <img
                  src={event.image || "/placeholder.svg?height=300&width=600"}
                  alt={event.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 right-4 bg-white px-3 py-2 rounded-full shadow-lg">
                  <span className="text-lg font-bold text-green-600">${event.price}</span>
                </div>
              </div>
            )}

            {/* Removed duration/guests/availability/add-ons grid */}
          </div>

          {/* Full Description */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">About This Experience</h3>
            <p className="text-gray-700 leading-relaxed">{event.detailedInfo.fullDescription}</p>
          </div>

          {/* What's Included */}
          <div className="mb-6">
            <button
              onClick={() => onSelect(event)}
              className="mb-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white py-2 px-6 rounded-full font-semibold hover:from-green-700 hover:to-emerald-700 transition duration-300"
            >
              BOOK NOW
            </button>
            <div className="grid md:grid-cols-2 gap-2">
              {event.detailedInfo.included.map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <svg
                    className="w-5 h-5 text-green-600 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Schedule (moved below Included) */}
          <div className="mb-6">
            <button
              onClick={() => onSelect(event)}
              className="mb-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white py-2 px-6 rounded-full font-semibold hover:from-green-700 hover:to-emerald-700 transition duration-300"
            >
              BOOK NOW
            </button>
            <div className="space-y-3">
              {event.detailedInfo.schedule.map((item, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
                  <div className="bg-green-600 text-white px-2 py-1 rounded text-sm font-medium min-w-fit">
                    {item.time}
                  </div>
                  <div className="text-gray-700">{item.activity}</div>
                </div>
              ))}
            </div>
          </div>

          {/* What to Bring */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">🎒 What to Bring</h3>
            <div className="grid md:grid-cols-2 gap-2">
              {event.detailedInfo.whatToBring.map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <svg
                    className="w-5 h-5 text-blue-600 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Dietary Notes */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">🍽️ Dietary Information</h3>
            <p className="text-gray-700 bg-yellow-50 p-3 rounded-lg">{event.detailedInfo.dietaryNotes}</p>
          </div>

          {/* Cancellation Policy */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">📋 Cancellation Policy</h3>
            <p className="text-gray-700 bg-red-50 p-3 rounded-lg">{event.detailedInfo.cancellationPolicy}</p>
          </div>

          {/* Available Add-ons */}
          {event.upsells.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">🎁 Available Add-ons</h3>
              <p className="text-sm text-gray-500 mb-3">Add-ons are priced per night or per session.</p>
              <div className="grid md:grid-cols-2 gap-3">
                {event.upsells.map((upsell) => (
                  <div key={upsell.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-900">{upsell.name}</span>
                      <span className="text-green-600 font-bold">+${upsell.price}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-4">
            <button
              onClick={() => onSelect(event)}
              className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 px-6 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transition duration-300"
            >
              Select This Experience - ${event.price}
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition duration-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// -------------------------
// Image slider for event cards (swipe before opening details)
const EventImageSlider = ({ images = [] }) => {
  const [index, setIndex] = useState(0)
  const touchStartRef = useRef(0)

  const next = () => {
    if (images.length === 0) return
    setIndex((prev) => (prev + 1) % images.length)
  }

  const prev = () => {
    if (images.length === 0) return
    setIndex((prev) => (prev - 1 + images.length) % images.length)
  }

  // Auto advance every 4 seconds
  useEffect(() => {
    if (images.length <= 1) return
    const id = setInterval(next, 4000)
    return () => clearInterval(id)
  }, [images])

  return (
    <div className="relative w-full h-full">
      {images.map((src, i) => (
        <div key={i} className={`absolute inset-0 transition-opacity duration-500 ${i === index ? 'opacity-100' : 'opacity-0'}`}>
          <img src={src} alt="Event" className="w-full h-full object-cover" />
        </div>
      ))}

      {/* Swipe overlay */}
      <div
        className="absolute inset-0"
        onTouchStart={(e) => (touchStartRef.current = e.touches[0].clientX)}
        onTouchEnd={(e) => {
          const diff = touchStartRef.current - e.changedTouches[0].clientX
          if (Math.abs(diff) > 50) {
            diff > 0 ? next() : prev()
          }
        }}
      />
    </div>
  )
}
// -------------------------

const Booking = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [selectedDate, setSelectedDate] = useState("")
  const [guests, setGuests] = useState(1)
  const [nights, setNights] = useState(2)
  const [selectedUpsells, setSelectedUpsells] = useState([])
  const [specialRequests, setSpecialRequests] = useState("")
  const [customerName, setCustomerName] = useState("")
  const [customerEmail, setCustomerEmail] = useState("")
  const [customerPhone, setCustomerPhone] = useState("")
  const [showEventDetails, setShowEventDetails] = useState(false)
  const [eventToShow, setEventToShow] = useState(null)
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [paymentDetails, setPaymentDetails] = useState(null)

  const eventIdFromQuery = new URLSearchParams(location.search).get("event")

  // Open modal automatically when ?event= param present
  useEffect(() => {
    if (eventIdFromQuery && !showEventDetails) {
      const ev = events.find((e) => e.id === eventIdFromQuery)
      if (ev) {
        setEventToShow(ev)
        setShowEventDetails(true)
      }
    }
  }, [eventIdFromQuery, showEventDetails])

  const bookingFormRef = useRef(null)

  // Automatically select dome usage for Leafwell Nature Stay when guests > 2
  useEffect(() => {
    if (selectedEvent?.id === "leafwell-nature-stay") {
      const domeUpsell = selectedEvent.upsells.find(upsell => upsell.id === "dome-usage")
      const isDomeSelected = selectedUpsells.find(upsell => upsell.id === "dome-usage")
      
      if (guests > 2 && domeUpsell && !isDomeSelected) {
        // Auto-select dome usage when guests > 2
        setSelectedUpsells(prev => [...prev, domeUpsell])
      } else if (guests <= 2 && isDomeSelected) {
        // Remove dome usage when guests <= 2
        setSelectedUpsells(prev => prev.filter(upsell => upsell.id !== "dome-usage"))
      }
    }
  }, [selectedEvent, guests, selectedUpsells])

  // Calculate total amount
  const calculateTotal = () => {
    if (!selectedEvent) return 0
    
    let basePrice
    if (selectedEvent.id === "leafwell-nature-stay") {
      // Special pricing for Leafwell Nature Stay based on nights
      const pricePerNight = nights === 1 ? selectedEvent.pricing.oneNight : selectedEvent.pricing.twoNights
      basePrice = pricePerNight * nights * guests
    } else {
      basePrice = selectedEvent.price * guests
    }
    
    const upsellTotal = selectedUpsells.reduce((total, upsell) => total + upsell.price, 0)
    return basePrice + upsellTotal
  }

  // Handle event selection
  const handleEventSelect = (event) => {
    setSelectedEvent(event)
    setSelectedDate("")
    setSelectedUpsells([])
    setNights(2)
    setCurrentStep(2)
    // Scroll to booking form
    setTimeout(() => {
      bookingFormRef.current?.scrollIntoView({ behavior: "smooth" })
    }, 100)
  }

  // Handle upsell toggle
  const toggleUpsell = (upsell) => {
    // Prevent unchecking dome usage for Leafwell Nature Stay when guests > 2
    if (selectedEvent?.id === "leafwell-nature-stay" && upsell.id === "dome-usage" && guests > 2) {
      const exists = selectedUpsells.find((item) => item.id === upsell.id)
      if (exists) {
        // Don't allow unchecking dome usage when guests > 2
        return
      }
    }

    setSelectedUpsells((prev) => {
      const exists = prev.find((item) => item.id === upsell.id)
      if (exists) {
        return prev.filter((item) => item.id !== upsell.id)
      } else {
        return [...prev, upsell]
      }
    })
  }

  // Handle payment success
  const handlePaymentSuccess = (details) => {
    setPaymentSuccess(true)
    setPaymentDetails(details)
    setCurrentStep(4)
  }

  // Get available dates for selected event
  const getAvailableDates = () => {
    if (!selectedEvent) return []
    return selectedEvent.availableDates.map((date) => ({
      value: date,
      label: new Date(date).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    }))
  }

  // Show event details modal
  const showEventDetailsModal = (event) => {
    setEventToShow(event)
    setShowEventDetails(true)
  }

  // Prepare booking data for payment
  const getBookingData = () => {
    return {
      eventId: selectedEvent?.id,
      eventType: selectedEvent?.name,
      eventDate: selectedDate,
      guests: guests,
      nights: nights,
      selectedUpsells: selectedUpsells,
      selectedUpsellNames: selectedUpsells.map((u) => u.name).join(", ") || "None",
      specialRequests: specialRequests,
      customerName: customerName,
      customerEmail: customerEmail,
      customerPhone: customerPhone,
      totalAmount: calculateTotal(),
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Book Your Experience</h1>
              <p className="text-gray-600 mt-2">Choose from our transformative wellness experiences</p>
            </div>
            <Link
              to="/"
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg transition duration-300"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-8">
          {[
            { step: 1, title: "Select Experience", icon: "🌿" },
            { step: 2, title: "Choose Details", icon: "📅" },
            { step: 3, title: "Payment", icon: "💳" },
            { step: 4, title: "Confirmation", icon: "✅" },
          ].map((item) => (
            <div key={item.step} className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                  currentStep >= item.step
                    ? "bg-green-600 text-white"
                    : currentStep === item.step
                      ? "bg-green-100 text-green-600 border-2 border-green-600"
                      : "bg-gray-200 text-gray-500"
                }`}
              >
                {currentStep > item.step ? "✓" : item.icon}
              </div>
              <div className="ml-2 hidden md:block">
                <div className={`text-sm font-medium ${currentStep >= item.step ? "text-green-600" : "text-gray-500"}`}>
                  {item.title}
                </div>
              </div>
              {item.step < 4 && <div className="w-8 h-0.5 bg-gray-300 mx-4 hidden md:block" />}
            </div>
          ))}
        </div>
      </div>

      {/* Step 1: Event Selection */}
      {currentStep === 1 && (
        <div className="max-w-7xl mx-auto px-4 pb-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6">
            {events.map((event) => (
              <div
                key={event.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition duration-300"
              >
                <div className="relative h-64">
                  <EventImageSlider images={event.images && event.images.length ? event.images : [event.image]} />
                  <div className="absolute top-4 right-4 bg-white px-3 py-2 rounded-full shadow-lg">
                    <span className="text-lg font-bold text-green-600">${event.price}</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{event.name}</h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">{event.description}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <span>⏱️ {event.duration}</span>
                    <span>👥 Max {event.maxGuests} guests</span>
                  </div>
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {event.highlights.slice(0, 3).map((highlight, index) => (
                        <span
                          key={index}
                          className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium"
                        >
                          {highlight}
                        </span>
                      ))}
                      {event.highlights.length > 3 && (
                        <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full font-medium">
                          +{event.highlights.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => showEventDetailsModal(event)}
                      className="flex-1 bg-gray-100 text-gray-800 py-2 px-4 rounded-lg font-semibold hover:bg-gray-200 transition duration-300"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => handleEventSelect(event)}
                      className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white py-2 px-4 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition duration-300"
                    >
                      Select
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Booking Details */}
      {currentStep === 2 && selectedEvent && (
        <div ref={bookingFormRef} className="max-w-4xl mx-auto px-4 pb-12">
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Complete Your Booking</h2>
              <button
                onClick={() => setCurrentStep(1)}
                className="text-gray-600 hover:text-gray-800 transition duration-300"
              >
                ← Change Experience
              </button>
            </div>

            {/* Selected Event Summary */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <div className="flex items-center space-x-4">
                <img
                  src={(selectedEvent.images && selectedEvent.images[0]) || selectedEvent.image || "/placeholder.svg?height=80&width=80"}
                  alt={selectedEvent.name}
                  className="w-16 h-16 rounded-lg object-cover"
                />
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">{selectedEvent.name}</h3>
                  <p className="text-sm text-gray-600">{selectedEvent.description}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                    <span>⏱️ {selectedEvent.duration}</span>
                    <span>💰 ${selectedEvent.price} per night</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Date Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Date *</label>
                  <select
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Choose a date...</option>
                    {getAvailableDates().map((date) => (
                      <option key={date.value} value={date.value}>
                        {date.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Number of Guests */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Number of Guests *</label>
                  <select
                    value={guests}
                    onChange={(e) => setGuests(Number.parseInt(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    {Array.from({ length: selectedEvent.maxGuests }, (_, i) => i + 1).map((num) => (
                      <option key={num} value={num}>
                        {num} {num === 1 ? "Guest" : "Guests"}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Number of Nights - Only for Leafwell Nature Stay */}
                {selectedEvent.id === "leafwell-nature-stay" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Number of Nights *</label>
                    <select
                      value={nights}
                      onChange={(e) => setNights(Number.parseInt(e.target.value))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value={1}>1 Night - $395/night</option>
                      <option value={2}>2 Nights - $315/night</option>
                      <option value={3}>3 Nights - $315/night</option>
                      <option value={4}>4 Nights - $315/night</option>
                      <option value={5}>5 Nights - $315/night</option>
                    </select>
                  </div>
                )}

                {/* Contact Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
                  <input
                    type="text"
                    placeholder="Full Name *"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <input
                    type="email"
                    placeholder="Email Address *"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number *"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Add-ons */}
                {selectedEvent.upsells.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Enhance Your Experience</h3>
                    <p className="text-sm text-gray-500 mb-3">Add-ons are priced per night or per session.</p>
                    <div className="space-y-3">
                      {selectedEvent.upsells.map((upsell) => {
                        const isSelected = !!selectedUpsells.find((item) => item.id === upsell.id)
                        const isDomeRequired = selectedEvent.id === "leafwell-nature-stay" && upsell.id === "dome-usage" && guests > 2
                        
                        return (
                          <div
                            key={upsell.id}
                            className={`border rounded-lg p-3 transition duration-300 ${
                              isSelected
                                ? "border-green-500 bg-green-50"
                                : "border-gray-200 hover:border-green-300"
                            } ${isDomeRequired ? "border-orange-500 bg-orange-50" : "cursor-pointer"}`}
                            onClick={() => !isDomeRequired && toggleUpsell(upsell)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => !isDomeRequired && toggleUpsell(upsell)}
                                  disabled={isDomeRequired}
                                  className={`h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded ${
                                    isDomeRequired ? "opacity-75 cursor-not-allowed" : ""
                                  }`}
                                />
                                <div>
                                  <span className="font-medium text-gray-900">{upsell.name}</span>
                                  {isDomeRequired && (
                                    <div className="text-xs text-orange-600 mt-1">Required for 3+ guests</div>
                                  )}
                                </div>
                              </div>
                              <span className="text-green-600 font-bold">+${upsell.price}</span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Special Requests */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Special Requests</label>
                  <textarea
                    value={specialRequests}
                    onChange={(e) => setSpecialRequests(e.target.value)}
                    placeholder="Any dietary restrictions, accessibility needs, or special requests..."
                    rows={4}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                {/* Booking Summary */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Booking Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>
                        {selectedEvent.id === "leafwell-nature-stay" 
                          ? `${nights} night${nights > 1 ? "s" : ""} × ${guests} guest${guests > 1 ? "s" : ""} × $${nights === 1 ? selectedEvent.pricing.oneNight : selectedEvent.pricing.twoNights}/night`
                          : `Base Price (${guests} guest${guests > 1 ? "s" : ""})`
                        }
                      </span>
                      <span>${selectedEvent.id === "leafwell-nature-stay" 
                        ? (nights === 1 ? selectedEvent.pricing.oneNight : selectedEvent.pricing.twoNights) * nights * guests
                        : selectedEvent.price * guests}</span>
                    </div>
                    {selectedUpsells.map((upsell) => (
                      <div key={upsell.id} className="flex justify-between text-green-600">
                        <span>{upsell.name}</span>
                        <span>+${upsell.price}</span>
                      </div>
                    ))}
                    <div className="border-t border-gray-200 pt-2 flex justify-between font-bold text-lg">
                      <span>Total</span>
                      <span className="text-green-600">${calculateTotal()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Continue Button */}
            <div className="mt-8 flex justify-between">
              <button
                onClick={() => setCurrentStep(1)}
                className="bg-gray-200 text-gray-800 px-6 py-3 rounded-xl font-semibold hover:bg-gray-300 transition duration-300"
              >
                ← Back to Events
              </button>
              <button
                onClick={() => setCurrentStep(3)}
                disabled={!selectedDate || !customerName || !customerEmail || !customerPhone}
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue to Payment →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Payment */}
      {currentStep === 3 && (
        <div className="max-w-4xl mx-auto px-4 pb-12">
          <Elements stripe={stripePromise}>
            <Payment
              bookingData={getBookingData()}
              onPaymentSuccess={handlePaymentSuccess}
              onCancel={() => setCurrentStep(2)}
            />
          </Elements>
        </div>
      )}

      {/* Step 4: Confirmation */}
      {currentStep === 4 && paymentSuccess && (
        <div className="max-w-4xl mx-auto px-4 pb-12">
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Booking Confirmed! 🎉</h2>
            <p className="text-gray-600 mb-6">
              Thank you for booking with Leafwell Farm. Your payment has been processed successfully and a confirmation
              email has been sent to {customerEmail}.
            </p>

            <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6 text-left">
              <h3 className="font-bold text-gray-900 mb-4">📋 Booking Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium">Experience:</span>
                  <span>{selectedEvent?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Date:</span>
                  <span>
                    {selectedDate
                      ? new Date(selectedDate).toLocaleDateString("en-US", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "Not selected"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Guests:</span>
                  <span>{guests}</span>
                </div>
                {selectedEvent?.id === "leafwell-nature-stay" && (
                  <div className="flex justify-between">
                    <span className="font-medium">Nights:</span>
                    <span>{nights} night{nights > 1 ? "s" : ""}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="font-medium">Total Paid:</span>
                  <span className="font-bold text-green-600">${calculateTotal()}</span>
                </div>
                {paymentDetails?.paymentIntent?.id && (
                  <div className="flex justify-between">
                    <span className="font-medium">Payment ID:</span>
                    <span className="text-xs text-gray-500">{paymentDetails.paymentIntent.id}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">📍 What's Next?</h3>
              <div className="text-sm text-blue-800 text-left space-y-1">
                <p>• Check your email for detailed confirmation and preparation instructions</p>
                <p>• We'll contact you 48 hours before your experience with final details</p>
                <p>• Arrive 15 minutes early on the day of your experience</p>
                <p>• Bring comfortable clothing and an open mind for transformation</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transition duration-300"
              >
                Return to Home
              </Link>
              <button
                onClick={() => {
                  setCurrentStep(1)
                  setSelectedEvent(null)
                  setSelectedDate("")
                  setGuests(1)
                  setNights(2)
                  setSelectedUpsells([])
                  setSpecialRequests("")
                  setCustomerName("")
                  setCustomerEmail("")
                  setCustomerPhone("")
                  setPaymentSuccess(false)
                  setPaymentDetails(null)
                }}
                className="bg-gray-200 text-gray-800 px-6 py-3 rounded-xl font-semibold hover:bg-gray-300 transition duration-300"
              >
                Book Another Experience
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event Details Modal */}
      <EventDetailsModal
        event={eventToShow}
        isOpen={showEventDetails}
        onClose={() => {
          if (eventIdFromQuery) {
            navigate("/", { replace: true })
          } else {
            setShowEventDetails(false)
          }
        }}
        onSelect={(event) => {
          setShowEventDetails(false)
          handleEventSelect(event)
          // Remove query param to avoid reopening modal
          if (eventIdFromQuery) {
            navigate("/booking", { replace: true })
          }
        }}
      />
    </div>
  )
}

export default Booking
